import { getGlowImageUrl, NftInfo } from "@glow/shared";
import { MarketplaceIcon } from "@glow/react/components/nfts/MarketplaceIcon";
import { useOnScreen } from "@lux/atoms/hooks/useOnScreen";

import React, { useEffect, useRef, useState } from "react";
import { solStringFromLamport } from "@glow/crypto";

export const NFTCard = ({
  info,
  subtitleMode = "price",
}: {
  info: NftInfo;
  subtitleMode?: "price" | "collection";
}) => {
  let desc: React.ReactNode | null = null;

  if (subtitleMode === "price") {
    if (info.price) {
      desc = (
        <div className={"flex-center"}>
          <div>{solStringFromLamport(info.price.lamports)}</div>

          <div className="flex ml-2">
            <MarketplaceIcon marketplace={info.marketplace} />
          </div>
        </div>
      );
    } else {
      desc = <span>Not for Sale</span>;
    }
  }

  if (subtitleMode === "collection" && info.collection_name) {
    desc = <span>{info.collection_name}</span>;
  }

  return (
    <div className="nft-card">
      <div
        className="image animated"
        style={
          info.image
            ? {
                backgroundImage: `url(${getGlowImageUrl({
                  url: info.image,
                  width: 480,
                  height: 480,
                })})`,
              }
            : {}
        }
      />

      <div className="info">
        <div className="name">{info.name || "Unknown NFT"}</div>
        <div className="desc">{desc}</div>
      </div>

      <style jsx>{`
        .nft-card {
          .image {
            width: 100%;
            padding-bottom: 100%;
            background-color: var(--secondary-bg-color);
            border-radius: 1rem;
            transform-origin: center;
            overflow: hidden;
            background-size: cover;
            background-position: center;
          }

          &:hover .image {
            transform: scale(1.02);
            box-shadow: var(--shadow-lg);
          }

          .info {
            padding-top: 0.5rem;
          }

          .name {
            color: var(--primary-color);
            font-weight: var(--font-weight-medium);
          }

          .desc {
            margin-top: 0.125rem;
            color: var(--secondary-color);
          }
        }
      `}</style>
    </div>
  );
};

export const NFTLoadingCard = ({ loadMore }: { loadMore?: () => void }) => {
  const loadMoreRef = useRef(null);
  const shouldLoadMore = useOnScreen(loadMoreRef);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (shouldLoadMore && loadMore && !loaded) {
      setLoaded(true);
      loadMore();
    }
  }, [shouldLoadMore, loaded, loadMore]);

  return (
    <div className="nft-card nft-loading-card shimmer-wrapper">
      <div className="image shimmer" ref={loadMoreRef} />
      <div className="info">
        <div className="name shimmer" />
        <div className="desc shimmer" />
      </div>

      <style jsx>{`
        .nft-card {
          .image {
            width: 100%;
            padding-bottom: 100%;
            border-radius: 1rem;
          }

          .name,
          .desc {
            border-radius: 0.25rem;
          }

          .name {
            height: 1rem;
            margin: 0.75rem 0 0.5rem 0;
            width: 75%;
          }

          .desc {
            height: 0.75rem;
            width: 50%;
          }
        }
      `}</style>
    </div>
  );
};
